import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';

import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CatalogLimitedType } from '../Common/CatalogLimitedType/CatalogLimitedType';
import { CatalogOthersType } from '../Common/CatalogOthersType';
import { CatalogPageToolbar } from '../Common/CatalogPageToolbar';
import { useRemoveOtherVariable } from '../Common/CatalogRemoveOtherVariable/hooks/useRemoveOtherVariable';
import { CatalogTable } from '../Common/CatalogTable';
import { useCommonTableData, useCommonVariablesColumns, useCommonVariablesFilters } from '../Common/CatalogTable/hooks';
import { CatalogTablePage } from '../Common/CatalogTablePage';
import { VariableGroups } from '../Common/CatalogVariableGroups';
import { CreateOrEditOtherVariable } from '../Common/CreateOrEditOtherVariable';
import { ViewOtherVariable } from '../Common/CreateOrEditOtherVariable/ViewOtherVariable';
import type { VariableIdType } from '../Common/types';

export const CatalogVariablesOtherUer = () => {
    const [isOpenOtherTypeModal, setIsOpenOtherTypeModal] = useState<boolean>(false);
    const [isOpenOtherLimitModal, setIsOpenOtherLimitModal] = useState<boolean>(false);
    const [isOpenVariableGroups, setIsOpenVariableGroups] = useState<boolean>(false);
    const [currentVariable, setCurrentVariable] = useState<VariableIdType>();
    const [currentViewVariableId, setCurrentViewVariableId] = useState<number | undefined>();

    const { catalogs: catalogsRules } = useAccess();

    const { id } = useParams();

    const {
        catalogName,
        catalogOtherCostVariablesData,
        currentPage,
        setCurrentPage,
        currentPageSize,
        setCurrentPageSize,
        totalPages,
        setQueryWhere,
        setQueryOrder,
        isLoading,
        isError,
        updateCatalogVariablesOther,
    } = useCommonTableData({
        catalogId: id,
        globalOnly: false,
        methodology: ProjectMethodologyEnum.Uer,
    });

    const filters = useCommonVariablesFilters({
        catalogId: id,
        globalOnly: false,
        methodology: ProjectMethodologyEnum.Uer,
    });

    const handleOpenCreateOrEditDialog = useCallback(
        (variableId: VariableIdType) => () => {
            setCurrentVariable(variableId);
        },
        [],
    );
    const toggleOpenViewVariableDialog = useCallback(
        (variableId: number | undefined) => () => {
            setCurrentViewVariableId(variableId);
        },
        [],
    );

    const toggleIsOpenVariableGroups = useCallback(() => {
        setIsOpenVariableGroups((p) => !p);
    }, []);

    const toggleIsOpenOtherTypeModal = useCallback(() => {
        setIsOpenOtherTypeModal((p) => !p);
    }, []);

    const toggleIsOpenOtherLimitModal = useCallback(() => {
        setIsOpenOtherLimitModal((p) => !p);
    }, []);

    const { isOpenConfirmationDialog, handleDelete, handleOpenRemoveDialog, handleCloseConfirmationDialog } =
        useRemoveOtherVariable({
            updateCatalogVariables: updateCatalogVariablesOther,
        });

    const columns = useCommonVariablesColumns({
        onEdit: handleOpenCreateOrEditDialog,
        onView: toggleOpenViewVariableDialog,
        onRemove: handleOpenRemoveDialog,
        catalogsRules,
    });

    return (
        <CatalogTablePage
            catalogName={catalogName}
            methodology={ProjectMethodologyEnum.Uer}
            isError={isError}
            isLoading={isLoading}
        >
            <CatalogPageToolbar
                handleOpenCreateOrEditDialog={handleOpenCreateOrEditDialog}
                handleOpenVariableGroups={toggleIsOpenVariableGroups}
                handleOpenTypeModal={toggleIsOpenOtherTypeModal}
                handleOpenLimitModal={toggleIsOpenOtherLimitModal}
            />
            {catalogOtherCostVariablesData && (
                <CatalogTable
                    tableColumns={columns}
                    catalogOtherCostVariablesData={catalogOtherCostVariablesData as any}
                    currentPageSize={currentPageSize}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    setCurrentPageSize={setCurrentPageSize}
                    setQueryOrder={setQueryOrder}
                    setQueryWhere={setQueryWhere}
                    filters={filters}
                />
            )}
            {typeof currentVariable !== 'undefined' && typeof id !== 'undefined' && (
                <CreateOrEditOtherVariable
                    id={currentVariable}
                    catalogUniqueId={id}
                    onClose={handleOpenCreateOrEditDialog(undefined)}
                    updateCatalogVariables={updateCatalogVariablesOther}
                    methodology={ProjectMethodologyEnum.Uer}
                />
            )}
            {typeof currentViewVariableId === 'number' && typeof id !== 'undefined' && (
                <ViewOtherVariable
                    id={currentViewVariableId}
                    onClose={toggleOpenViewVariableDialog(undefined)}
                    methodology={ProjectMethodologyEnum.Uer}
                />
            )}
            {isOpenOtherTypeModal && (
                <CatalogOthersType onClose={toggleIsOpenOtherTypeModal} methodology={ProjectMethodologyEnum.Uer} />
            )}
            {isOpenOtherLimitModal && (
                <CatalogLimitedType onClose={toggleIsOpenOtherLimitModal} methodology={ProjectMethodologyEnum.Uer} />
            )}
            {isOpenVariableGroups && (
                <VariableGroups
                    onClose={toggleIsOpenVariableGroups}
                    catalogUniqueId={id}
                    methodology={ProjectMethodologyEnum.Uer}
                />
            )}
            {isOpenConfirmationDialog && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={handleDelete}
                    onClose={handleCloseConfirmationDialog}
                    textHeader="Удаление переменной"
                    textConfirmation="Вы уверены, что хотите удалить переменную?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}
        </CatalogTablePage>
    );
};

CatalogVariablesOtherUer.displayName = 'CatalogVariablesOtherUer';
